import React, { useEffect, useState } from "react";
import client from "../../utils/API";
import StLoewe from "../shared/StLoewe";
import {
  Container,
  Box,
  makeStyles,
  Typography,
  Button
} from "@material-ui/core";
import StSlider from "../shared/StSlider";
import StTermin from "../shared/StTermin";
import StNews from "../shared/StNews";
import { SortOrder } from "@kentico/kontent-delivery";
import { useTheme } from "@material-ui/styles";
import Soldat from "../../assets/soldat.png";
import Logo from "../../assets/logo.png";
import useScreensize from "../../hooks/useScreensize";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { grey } from "@material-ui/core/colors";
import { Link } from "react-router-dom";

const useStyles = makeStyles(theme => ({
  keyvisual: {
    backgroundSize: "cover"
  },
  soldat: {
    height: "calc(100% + 64px)",
    width: "auto",
    position: "absolute",
    left: -40,
    top: -64,
    zIndex: 1,
    [theme.breakpoints.down("xs")]: {
      left: 8,
      zIndex: 1
    }
  },
  logo: {
    width: "40%",
    height: "auto",
    position: "absolute",
    right: "5%",
    top: 30
  },
  headlineAktuelles: {
    color: "white",
    marginBottom: theme.spacing(4)
  },
  headlineLoewen: {
    color: grey[500],
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 3)
  }
}));

const headerHeights = { xs: 100, sm: 250, md: 300, lg: 450, xl: 600 };

const Home = () => {
  const [loewen, setLoewen] = useState([]);
  const [termine, setTermine] = useState([]);
  const [news, setNews] = useState([]);
  const [slider, setSlider] = useState(null);
  const [keyvisual, setKeyvisual] = useState(null);
  const classes = useStyles();
  const theme = useTheme();
  const { xs, sm, md, lg, xl, current } = useScreensize();
  const [soldatStyle, setSoldatStyle] = useState({ opacity: 1 });

  useEffect(() => {
    fetchLoewen();
    fetchSlider();
    fetchTermine();
    fetchNews();
    fetchKeyvisual();
  }, []);

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const headerHeight = headerHeights[current];
      let opacity = 1;
      const factor = (headerHeight - currPos.y * -2) / headerHeight;
      opacity *= factor;
      // setSoldatStyle({ opacity });
    },
    [soldatStyle, current]
  );

  const fetchLoewen = () => {
    client
      .items()
      .type("lowe")
      .toObservable()
      .subscribe(response => {
        setLoewen(
          response.items.sort(
            (a, b) =>
              a.ausgezeichnet_im_jahr.value - b.ausgezeichnet_im_jahr.value
          )
        );
      });
  };

  const fetchTermine = () => {
    client
      .items()
      .type("termin")
      .limitParameter(4)
      .orderParameter("elements.veranstaltungsdatum", SortOrder.desc)
      .toObservable()
      .subscribe(response => {
        setTermine(response.items);
      });
  };

  const fetchNews = () => {
    client
      .items()
      .type("news")
      .limitParameter(4)
      .orderParameter("elements.datum", SortOrder.desc)
      .toObservable()
      .subscribe(response => {
        setNews(response.items);
      });
  };

  const fetchSlider = () => {
    client
      .items()
      .type("slider")
      .equalsFilter("elements.identifier", "home")
      .toObservable()
      .subscribe(response => {
        setSlider(response.items ? response.items[0] : null);
      });
  };

  const fetchKeyvisual = () => {
    client
      .items()
      .type("keyvisual")
      .equalsFilter("elements.identifier", "home")
      .toObservable()
      .subscribe(response => {
        setKeyvisual(response.items ? response.items[0] : null);
      });
  };

  return (
    <>
      {Boolean(keyvisual && keyvisual.bild) && (
        <Box
          display="flex"
          flexDirection={xs ? "column" : "row"}
          bgcolor={theme.palette.primary.dark}
          height={xs ? "auto" : headerHeights[current]}
        >
          <Box
            className={classes.keyvisual}
            flexShrink={0}
            width={{ xs: "100%", sm: "60%" }}
            style={{
              backgroundImage: "url(" + keyvisual.bild.value[0].url + ")"
            }}
            height={{ xs: 200, sm: "auto" }}
          ></Box>
          <Box
            position="relative"
            flexGrow={1}
            height={{ xs: 200, sm: "auto" }}
          >
            <img
              src={Soldat}
              className={classes.soldat}
              style={xs ? undefined : soldatStyle}
              alt=""
            />
            <img src={Logo} className={classes.logo} alt="" />
          </Box>
        </Box>
      )}
      <Container maxWidth="lg">
        <Box
          display="flex"
          flexDirection={xs || sm || md ? "column" : "row"}
          py={{ xs: 3, md: 4, xl: 8 }}
        >
          <Box
            width={xs || sm || md ? "100%" : "66%"}
            flexShrink={0}
            mr={{ xs: 0, sm: 4 }}
            mb={{ xs: 3, lg: 0 }}
          >
            {Boolean(slider) && (
              <StSlider images={slider.bilder.value.map(b => b.url)} />
            )}
          </Box>
          <Box flexGrow={1}>
            {termine.map((t, idx) => (
              <React.Fragment key={idx}>
                <StTermin item={t} />
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Container>
      <Box
        bgcolor={theme.palette.primary.dark}
        py={{ xs: 4, sm: 6, md: 6, xl: 8 }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            className={classes.headlineAktuelles}
          >
            Aktuelles
          </Typography>
          <Box
            display="flex"
            flexDirection={xs ? "column" : "row"}
            flexWrap="wrap"
          >
            {news.map((n, idx) => (
              <Box
                width={xs || sm || md ? "100%" : "50%"}
                flexShrink={0}
                py={{ xs: 2, sm: 4 }}
                px={{ xs: 0, sm: 4 }}
                key={idx}
              >
                <StNews item={n} />
              </Box>
            ))}
          </Box>
          <Box display="flex" p={4} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/veranstaltungen-aktuelles"
            >
              Weitere
            </Button>
          </Box>
        </Container>
      </Box>

      <Box bgcolor="white" py={{ xs: 2, md: 4, xl: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            className={classes.headlineLoewen}
          >
            Unsere Träger des „Goldenen Löwen“
          </Typography>
          <Box
            display="flex"
            flexDirection={xs ? "column" : "row"}
            flexWrap="wrap"
          >
            {loewen.map((l, idx) => (
              <Box
                width={xs || sm || md ? "100%" : "50%"}
                flexShrink={0}
                py={{ xs: 2, sm: 4 }}
                px={{ xs: 0, sm: 4 }}
                key={idx}
              >
                <StLoewe item={l} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Home;
